import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { getKategorijePartnera } from '../../lib/services/partnerService';
import Icon from '../../components/AppIcon';

import DashboardSidebar from '../../components/navigation/DashboardSidebarAdmin';
import DashboardHeader from '../../components/navigation/DashboardHeader';
import DashboardFooter from '../../components/navigation/DashboardFooter';
import MobileMenuButton from '../../components/navigation/MobileMenuButton';

const ITEMS_PER_PAGE = 15;
const STORAGE_BUCKET = 'aziende';

// ── Standardni tipovi partnera: vrijednost (baza) je fiksni kod,
// labela dolazi iz t('listapartner.types.<code>'). Custom kategorije iz
// baze (partneri_kategorije) čuvaju već ljudski čitljiv naziv u tip_partnera,
// pa se prikazuju bez prevoda.
const STANDARD_TYPE_CODES = ['dobavljac', 'klijent', 'obostrano'];

// ── CUSTOM CONFIRM MODAL (brisanje partnera + SVIH njegovih dokumenata) ─────
// Ovo je destruktivna, nepovratna akcija koja briše i partnera i CIJELU
// njegovu arhivu dokumenata (bazu + fajlove u Storage-u), zato modal mora
// jasno i vizuelno naglasiti razmjere akcije prije nego korisnik potvrdi.
const ConfirmDeleteModal = ({ partner, docsCount, onConfirm, onCancel, deleting, t }) => (
  <div className="fixed inset-0 z-[60] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
    <div className="bg-card border-2 border-destructive/40 p-6 rounded-2xl max-w-sm w-full shadow-2xl space-y-5 animate-in fade-in zoom-in duration-200">
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-destructive/10 text-destructive rounded-xl">
          <Icon name="AlertTriangle" size={22} />
        </div>
        <h3 className="font-black uppercase text-sm tracking-wider text-destructive">
          {t('listapartner.confirm_delete_title', { defaultValue: 'Potvrda Brisanja' })}
        </h3>
      </div>

      <div className="space-y-3">
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t('listapartner.confirm_delete', { defaultValue: 'Da li ste sigurni da želite trajno obrisati ovog partnera?' })}
          {partner?.naziv_partnera && (
            <>
              <br />
              <span className="font-bold text-foreground break-all">"{partner.naziv_partnera}"</span>
            </>
          )}
        </p>

        <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-xl">
          <p className="text-xs font-bold text-destructive leading-relaxed">
            {docsCount > 0
              ? t('listapartner.confirm_delete_docs_warning', {
                  count: docsCount,
                  defaultValue: `PAŽNJA: Ova akcija će trajno obrisati i svih ${docsCount} dokumenata ovog partnera (iz baze i sa servera). Ovo se NE MOŽE poništiti.`
                })
              : t('listapartner.confirm_delete_no_docs_warning', {
                  defaultValue: 'PAŽNJA: Ova akcija se NE MOŽE poništiti.'
                })
            }
          </p>
        </div>
      </div>

      <div className="flex gap-3 pt-1">
        <button
          onClick={onCancel}
          disabled={deleting}
          className="flex-1 py-2.5 border border-border rounded-xl text-xs font-bold uppercase tracking-wider text-muted-foreground hover:bg-muted hover:text-foreground transition-all disabled:opacity-50"
        >
          {t('common.no', { defaultValue: 'Ne' })}
        </button>
        <button
          onClick={onConfirm}
          disabled={deleting}
          className="flex-1 py-2.5 bg-destructive text-white rounded-xl text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {deleting
            ? <><Icon name="Loader2" size={14} className="animate-spin" /> {t('common.delete', { defaultValue: 'Obriši' })}...</>
            : <>{t('common.yes', { defaultValue: 'Da' })}, {t('common.delete', { defaultValue: 'Obriši' })}</>
          }
        </button>
      </div>
    </div>
  </div>
);

// ── GLAVNA KOMPONENTA ────────────────────────────────────────────────────────
const PartnersCompanyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [partneri, setPartneri] = useState([]);
  const [kategorije, setKategorije] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Info / Uređivanje modal
  const [selectedPartnerForInfo, setSelectedPartnerForInfo] = useState(null);
  const [isEditingPartner, setIsEditingPartner] = useState(false);
  const [savingPartner, setSavingPartner] = useState(false);
  const [editForm, setEditForm] = useState({
    naziv_partnera: '', tip_partnera: '', id_kategorije: '', pib_jib: '', pdv_broj: '',
    broj_registracije: '', adresa: '', grad: '', postanski_broj: '', drzava: '',
    email: '', telefon: '', web_stranica: '', bankovni_racun: '', naziv_banke: '',
    kontakt_osoba: '', kontakt_telefon: '', napomena: ''
  });

  // Confirm delete modal (cascade: partner + svi njegovi dokumenti)
  const [partnerToDelete, setPartnerToDelete] = useState(null);
  const [partnerDocsCount, setPartnerDocsCount] = useState(0);
  const [deletingId, setDeletingId] = useState(null);

  // Filteri
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('all');

  // ── Helper za prevod tipa partnera ───────────────────────────────────────────
  const getTipPartneraLabel = useCallback((code) => {
    if (!code) return '';
    if (STANDARD_TYPE_CODES.includes(code)) {
      return t(`listapartner.types.${code}`, { defaultValue: code });
    }
    // Custom kategorija iz baze — već ljudski čitljiv naziv, nema prevoda
    return code;
  }, [t]);

  // ── Fetch data ─────────────────────────────────────────────────────────────
  const fetchInitialData = useCallback(async (pageNumber = 0) => {
    if (pageNumber === 0) setLoading(true);
    else setLoadingMore(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile, error: profileErr } = await supabase
        .from('profili').select('*').eq('id', user.id).single();
      if (profileErr) throw profileErr;
      setUserData(profile);
      if (!profile?.id_azienda) { setLoading(false); return; }

      const from = pageNumber * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      const { data: partneriData, error: partneriErr } = await supabase
        .from('partneri')
        .select('*')
        .eq('id_aziende', profile.id_azienda)
        .order('created_at', { ascending: false })
        .range(from, to);
      if (partneriErr) throw partneriErr;

      if (pageNumber === 0) setPartneri(partneriData || []);
      else setPartneri(prev => [...prev, ...(partneriData || [])]);
      setHasMore((partneriData || []).length === ITEMS_PER_PAGE);

      const katResult = await getKategorijePartnera(profile.id_azienda);
      if (katResult.success) setKategorije(katResult.data);
    } catch (err) {
      console.error('Errore fetch partneri:', err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { fetchInitialData(0); }, [fetchInitialData]);

  // ── Info modal ─────────────────────────────────────────────────────────────
  const handleOpenInfoModal = (partner) => {
    setSelectedPartnerForInfo(partner);
    setIsEditingPartner(false);
    setEditForm({
      naziv_partnera: partner.naziv_partnera || '',
      tip_partnera: partner.tip_partnera || '',
      id_kategorije: partner.id_kategorije || '',
      pib_jib: partner.pib_jib || '',
      pdv_broj: partner.pdv_broj || '',
      broj_registracije: partner.broj_registracije || '',
      adresa: partner.adresa || '',
      grad: partner.grad || '',
      postanski_broj: partner.postanski_broj || '',
      drzava: partner.drzava || '',
      email: partner.email || '',
      telefon: partner.telefon || '',
      web_stranica: partner.web_stranica || '',
      bankovni_racun: partner.bankovni_racun || '',
      naziv_banke: partner.naziv_banke || '',
      kontakt_osoba: partner.kontakt_osoba || '',
      kontakt_telefon: partner.kontakt_telefon || '',
      napomena: partner.napomena || ''
    });
  };

  // Rukovanje promjenom tipa/kategorije u edit formi Info modala —
  // isti princip kao u CreaPartner.jsx: ako je izabrana kategorija iz baze,
  // čuva se i njen id_kategorije; ako je standardni tip, id_kategorije se briše.
  const handleEditTipChange = (e) => {
    const value = e.target.value;
    const selectedKat = kategorije.find((k) => String(k.id) === value);

    if (selectedKat) {
      setEditForm(prev => ({
        ...prev,
        tip_partnera: selectedKat.naziv_kategorije,
        id_kategorije: selectedKat.id
      }));
    } else {
      setEditForm(prev => ({
        ...prev,
        tip_partnera: value,
        id_kategorije: ''
      }));
    }
  };

  const handleSavePartnerEdits = async () => {
    if (!selectedPartnerForInfo) return;
    if (!editForm.naziv_partnera.trim()) {
      alert(t('listapartner.errors.name_req', 'Naziv partnera je obavezan'));
      return;
    }

    setSavingPartner(true);
    try {
      const updatedFields = {
        naziv_partnera: editForm.naziv_partnera.trim(),
        tip_partnera: editForm.tip_partnera || null,
        id_kategorije: editForm.id_kategorije || null,
        pib_jib: editForm.pib_jib || null,
        pdv_broj: editForm.pdv_broj || null,
        broj_registracije: editForm.broj_registracije || null,
        adresa: editForm.adresa || null,
        grad: editForm.grad || null,
        postanski_broj: editForm.postanski_broj || null,
        drzava: editForm.drzava || null,
        email: editForm.email || null,
        telefon: editForm.telefon || null,
        web_stranica: editForm.web_stranica || null,
        bankovni_racun: editForm.bankovni_racun || null,
        naziv_banke: editForm.naziv_banke || null,
        kontakt_osoba: editForm.kontakt_osoba || null,
        kontakt_telefon: editForm.kontakt_telefon || null,
        napomena: editForm.napomena || null
      };

      const { data, error } = await supabase
        .from('partneri')
        .update(updatedFields)
        .eq('id', selectedPartnerForInfo.id)
        .select().single();
      if (error) throw error;

      setPartneri(prev => prev.map(p => p.id === data.id ? data : p));
      setSelectedPartnerForInfo(data);
      setIsEditingPartner(false);
      alert(t('listapartner.update_success', { defaultValue: 'Partner je uspješno ažuriran!' }));
    } catch (err) {
      alert(t('listapartner.update_error', { defaultValue: 'Greška pri ažuriranju partnera' }) + ': ' + err.message);
    } finally {
      setSavingPartner(false);
    }
  };

  // ── Zahtjev za brisanje: prije prikaza modala, dohvati broj dokumenata
  // partnera da bi upozorenje bilo konkretno (ne generičko), npr.
  // "obrisat ćete i 12 dokumenata", umjesto praznog "obrisat ćete sve".
  const handleRequestDelete = async (partner) => {
    setPartnerToDelete(partner);
    setPartnerDocsCount(0);
    try {
      const { count, error } = await supabase
        .from('documenti_partnera')
        .select('id', { count: 'exact', head: true })
        .eq('id_partnera', partner.id)
        .eq('id_azienda', userData?.id_azienda);
      if (error) throw error;
      setPartnerDocsCount(count || 0);
    } catch (err) {
      console.warn('Nije moguće izbrojati dokumente partnera prije brisanja:', err.message);
    }
  };

  // ── Delete (CASCADE): partner + SVI njegovi dokumenti ───────────────────────
  // Redoslijed je bitan:
  // 1) Dohvati file_path svih dokumenata partnera.
  // 2) Trajno obriši te fajlove iz Storage bucketa (batch remove).
  // 3) Trajno obriši redove iz 'documenti_partnera'.
  // 4) Trajno obriši samog partnera iz 'partneri'.
  // Ako storage brisanje djelimično ne uspije, logiramo upozorenje i
  // NASTAVLJAMO — orphan fajl u storage-u je manji problem od nemogućnosti
  // da korisnik uopšte obriše partnera.
  const confirmDelete = async () => {
    if (!partnerToDelete || !userData?.id_azienda) return;
    setDeletingId(partnerToDelete.id);
    try {
      const { data: partnerDocs, error: docsFetchErr } = await supabase
        .from('documenti_partnera')
        .select('file_path')
        .eq('id_partnera', partnerToDelete.id)
        .eq('id_azienda', userData.id_azienda);
      if (docsFetchErr) throw docsFetchErr;

      const filePaths = (partnerDocs || []).map(d => d.file_path).filter(Boolean);
      if (filePaths.length > 0) {
        const { error: storageErr } = await supabase.storage
          .from(STORAGE_BUCKET)
          .remove(filePaths);
        if (storageErr) {
          console.warn('Upozorenje: brisanje fajlova partnera iz storage-a nije uspjelo:', storageErr.message);
        }
      }

      const { error: docsDeleteErr } = await supabase
        .from('documenti_partnera')
        .delete()
        .eq('id_partnera', partnerToDelete.id)
        .eq('id_azienda', userData.id_azienda);
      if (docsDeleteErr) throw docsDeleteErr;

      const { error: partnerDeleteErr } = await supabase
        .from('partneri')
        .delete()
        .eq('id', partnerToDelete.id)
        .eq('id_aziende', userData.id_azienda);
      if (partnerDeleteErr) throw partnerDeleteErr;

      setPartneri(prev => prev.filter(p => p.id !== partnerToDelete.id));
      if (selectedPartnerForInfo?.id === partnerToDelete.id) setSelectedPartnerForInfo(null);
      setPartnerToDelete(null);
      setPartnerDocsCount(0);
    } catch (err) {
      alert(t('listapartner.delete_error', { defaultValue: 'Greška pri brisanju partnera' }) + ': ' + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  // ── Memoized values ────────────────────────────────────────────────────────
  const realnoPostojeciTipovi = useMemo(() => {
    const tipovi = partneri.map(p => p.tip_partnera).filter(Boolean);
    return [...new Set(tipovi)].sort();
  }, [partneri]);

  const filteredPartneri = useMemo(() => {
    return partneri.filter(partner => {
      const s = searchTerm.toLowerCase();
      const tipLabel = getTipPartneraLabel(partner.tip_partnera).toLowerCase();
      const matchesSearch =
        partner.naziv_partnera?.toLowerCase().includes(s) ||
        partner.pib_jib?.toLowerCase().includes(s) ||
        partner.pdv_broj?.toLowerCase().includes(s) ||
        partner.email?.toLowerCase().includes(s) ||
        partner.telefon?.toLowerCase().includes(s) ||
        partner.grad?.toLowerCase().includes(s) ||
        tipLabel.includes(s);
      const matchesTipo = filterTipo === 'all' || partner.tip_partnera === filterTipo;
      return matchesSearch && matchesTipo;
    });
  }, [partneri, searchTerm, filterTipo, getTipPartneraLabel]);

  // ── Loading screen ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col justify-center items-center text-primary">
        <Icon name="Loader2" size={40} className="animate-spin mb-4" />
        <p className="text-muted-foreground text-xs font-black uppercase tracking-widest">
          {t('common.loading', { defaultValue: 'Učitavanje...' })}
        </p>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background text-foreground">
      <MobileMenuButton isOpen={isSidebarOpen} onClick={() => setIsSidebarOpen(!isSidebarOpen)} />
      <DashboardSidebar isCollapsed={!isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)} />
      <DashboardHeader userName={userData?.nome ? `${userData.nome} ${userData.cognome}` : 'Admin'} />

      <main className="main-content with-sidebar py-10 px-4 md:px-8 mt-16">
        <div className="max-w-7xl mx-auto space-y-8">

          {/* Zaglavlje */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <button
              onClick={() => navigate(-1)}
              className="group flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-bold uppercase text-[10px] tracking-widest"
            >
              <div className="p-2 bg-muted rounded-xl group-hover:bg-primary/10 transition-colors">
                <Icon name="ArrowLeft" size={16} />
              </div>
              {t('listapartner.back', { defaultValue: 'Nazad' })}
            </button>
          </div>

          {/* Naslov + dugme "NOVI PARTNER" na desnoj strani */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                <Icon name="Handshake" className="text-primary" size={32} />
                {t('listapartner.title', { defaultValue: 'Poslovni Partneri' })}
              </h1>
              <p className="text-xs text-muted-foreground mt-1 font-medium">
                {t('listapartner.subtitle', { defaultValue: 'Upravljanje klijentima i dobavljačima vaše firme' })}
              </p>
              <div className="h-1 w-20 bg-primary mt-3 rounded-full"></div>
            </div>

            <button
              onClick={() => navigate('/crea-partner')}
              className="shrink-0 flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 rounded-2xl font-bold uppercase text-xs tracking-widest hover:opacity-90 transition-all shadow-lg shadow-primary/20"
            >
              <Icon name="UserPlus" size={18} />
              {t('listapartner.new_partner', { defaultValue: 'Novi Partner' })}
            </button>
          </div>

          {/* Sadržaj: Tabela (full width) */}
          <div className="space-y-4">

            {/* Filteri */}
            <div className="bg-card p-4 rounded-2xl border border-border shadow-sm flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-[2] w-full">
                <label className="text-[10px] font-black uppercase text-muted-foreground mb-1.5 block ml-1 tracking-widest">
                  {t('listapartner.filter_archive', { defaultValue: 'Pretraga Partnera' })}
                </label>
                <div className="relative">
                  <Icon name="Search" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
                  <input
                    type="text"
                    placeholder={t('listapartner.search_placeholder', { defaultValue: 'Traži po nazivu, PIB/JIB, gradu, emailu...' })}
                    className="w-full pl-11 pr-4 py-2.5 bg-muted/30 border border-border rounded-xl text-sm outline-none focus:bg-background transition-all font-medium"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex-1 w-full">
                <label className="text-[10px] font-black uppercase text-muted-foreground mb-1.5 block ml-1 tracking-widest">
                  {t('listapartner.type', { defaultValue: 'Tip Partnera' })}
                </label>
                <select
                  className="w-full p-2.5 bg-muted/30 border border-border rounded-xl text-sm outline-none font-bold truncate"
                  value={filterTipo}
                  onChange={e => setFilterTipo(e.target.value)}
                >
                  <option value="all">{t('common.all', { defaultValue: 'Svi' })}</option>
                  {realnoPostojeciTipovi.map(tip => (
                    <option key={tip} value={tip}>{getTipPartneraLabel(tip)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tabela */}
            <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden flex flex-col">
              <div className="p-4 border-b border-border bg-muted/10 flex items-center justify-between">
                <h3 className="font-bold flex items-center gap-2 uppercase text-xs tracking-widest text-muted-foreground">
                  <Icon name="Handshake" size={16} className="text-primary" />
                  {t('listapartner.list_title', { defaultValue: 'Lista Partnera' })}
                </h3>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/10 text-primary px-2.5 py-1 rounded-lg">
                  Total: {filteredPartneri.length}
                </span>
              </div>

              <div className="overflow-x-auto overflow-y-auto max-h-[600px]">
                {filteredPartneri.length === 0 ? (
                  <div className="p-12 text-center text-muted-foreground">
                    <Icon name="UsersRound" size={40} className="mx-auto mb-3 opacity-30" />
                    <p className="text-xs font-bold uppercase tracking-widest">
                      {t('listapartner.no_partners_found', { defaultValue: 'Nema pronađenih partnera za odabrane kriterije.' })}
                    </p>
                  </div>
                ) : (
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-border bg-muted/30 text-[10px] uppercase font-black tracking-widest text-muted-foreground">
                        <th className="p-3.5 pl-4">{t('listapartner.col_name', { defaultValue: 'Naziv Partnera' })}</th>
                        <th className="p-3.5">{t('listapartner.col_piva', { defaultValue: 'P.IVA / CF' })}</th>
                        <th className="p-3.5">{t('listapartner.col_type', { defaultValue: 'Tip' })}</th>
                        <th className="p-3.5">{t('listapartner.col_location', { defaultValue: 'Grad / Adresa' })}</th>
                        <th className="p-3.5">{t('listapartner.col_contact', { defaultValue: 'Kontakt' })}</th>
                        <th className="p-3.5 pr-4 text-right">{t('common.actions', { defaultValue: 'Akcije' })}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border text-sm">
                      {filteredPartneri.map(partner => (
                        <tr key={partner.id} className="hover:bg-muted/20 transition-colors group">

                          <td className="p-3.5 pl-4 font-bold text-foreground">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-primary/10 text-primary rounded-lg shrink-0">
                                <Icon name="Building2" size={18} />
                              </div>
                              <div className="min-w-0">
                                <p className="truncate max-w-[160px] text-xs font-bold" title={partner.naziv_partnera}>
                                  {partner.naziv_partnera}
                                </p>
                                {partner.kontakt_osoba && (
                                  <p className="text-[10px] text-muted-foreground font-normal truncate max-w-[150px]">
                                    {partner.kontakt_osoba}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className="p-3.5">
                            {partner.pib_jib
                              ? <span className="text-[10px] font-bold font-mono bg-muted px-2 py-0.5 rounded-md border border-border">{partner.pib_jib}</span>
                              : <span className="text-[10px] opacity-30">{t('common.nd', { defaultValue: 'N/D' })}</span>
                            }
                          </td>

                          <td className="p-3.5">
                            {partner.tip_partnera
                              ? (
                                <span className="inline-block bg-muted text-foreground text-[10px] font-black uppercase px-2.5 py-1 rounded-md border border-border">
                                  {getTipPartneraLabel(partner.tip_partnera)}
                                </span>
                              )
                              : <span className="text-[10px] opacity-30">{t('common.nd', { defaultValue: 'N/D' })}</span>
                            }
                          </td>

                          <td className="p-3.5 text-xs text-muted-foreground">
                            {partner.grad || partner.adresa ? (
                              <>
                                {partner.grad && <span className="font-bold text-foreground block">{partner.grad}</span>}
                                {partner.adresa && <span className="text-[10px] truncate max-w-[150px] block">{partner.adresa}</span>}
                              </>
                            ) : (
                              <span className="text-[10px] opacity-30">{t('common.nd', { defaultValue: 'N/D' })}</span>
                            )}
                          </td>

                          <td className="p-3.5 text-xs text-muted-foreground">
                            {partner.telefon || partner.email ? (
                              <>
                                {partner.telefon && <span className="font-bold text-foreground block">{partner.telefon}</span>}
                                {partner.email && <span className="text-[10px] truncate max-w-[150px] block">{partner.email}</span>}
                              </>
                            ) : (
                              <span className="text-[10px] opacity-30">{t('common.nd', { defaultValue: 'N/D' })}</span>
                            )}
                          </td>

                          <td className="p-3.5 pr-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => navigate(`/partner/${partner.id}`)}
                                className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg text-muted-foreground transition-all"
                                title={t('listapartner.view_documents', { defaultValue: 'Dokumenti Partnera' })}
                              >
                                <Icon name="FolderOpen" size={16} />
                              </button>
                              <button
                                onClick={() => handleOpenInfoModal(partner)}
                                className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg text-muted-foreground transition-all"
                                title={t('common.edit', { defaultValue: 'Detalji' })}
                              >
                                <Icon name="Info" size={16} />
                              </button>
                              <button
                                onClick={() => handleRequestDelete(partner)}
                                className="p-2 hover:bg-destructive/10 hover:text-destructive rounded-lg text-muted-foreground transition-all"
                                title={t('common.delete', { defaultValue: 'Obriši' })}
                              >
                                <Icon name="Trash2" size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {hasMore && (
                <div className="p-3 border-t border-border text-center bg-muted/5">
                  <button
                    onClick={() => { const next = page + 1; setPage(next); fetchInitialData(next); }}
                    disabled={loadingMore}
                    className="text-xs font-black uppercase tracking-widest text-primary hover:underline flex items-center justify-center gap-2 mx-auto py-1"
                  >
                    {loadingMore && <Icon name="Loader2" size={14} className="animate-spin" />}
                    {t('common.load_more', { defaultValue: 'Prikaži više' })}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <DashboardFooter />

      {/* ── MODAL: Confirm Delete (Cascade: partner + svi dokumenti) ────────── */}
      {partnerToDelete && (
        <ConfirmDeleteModal
          partner={partnerToDelete}
          docsCount={partnerDocsCount}
          onConfirm={confirmDelete}
          onCancel={() => { setPartnerToDelete(null); setPartnerDocsCount(0); }}
          deleting={deletingId === partnerToDelete.id}
          t={t}
        />
      )}

      {/* ── MODAL: Info / Uređivanje Partnera ────────────────────────────────── */}
      {selectedPartnerForInfo && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 rounded-2xl max-w-lg w-full space-y-6 shadow-2xl animate-in fade-in zoom-in duration-200">

            {/* Header */}
            <div className="flex items-center justify-between border-b border-border pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-primary/10 text-primary rounded-xl">
                  <Icon name={isEditingPartner ? 'Edit3' : 'Building2'} size={22} />
                </div>
                <div>
                  <h3 className="font-black uppercase text-sm tracking-wider text-foreground">
                    {isEditingPartner ? t('listapartner.edit_modal_title', { defaultValue: 'Uredi Partnera' }) : t('listapartner.info_modal_title', { defaultValue: 'Detalji Partnera' })}
                  </h3>
                  <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest mt-0.5">
                    {isEditingPartner ? t('listapartner.edit_subtitle', { defaultValue: 'Izmjena podataka' }) : t('listapartner.info_subtitle', { defaultValue: 'Pregled podataka' })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!isEditingPartner && (
                  <button
                    onClick={() => setIsEditingPartner(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary hover:bg-primary/20 rounded-xl text-xs font-bold uppercase tracking-wider transition-all"
                  >
                    <Icon name="Edit" size={14} /> {t('common.edit', { defaultValue: 'Uredi' })}
                  </button>
                )}
                <button
                  onClick={() => { setSelectedPartnerForInfo(null); setIsEditingPartner(false); }}
                  className="p-1.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
            </div>

            {/* Sadržaj */}
            <div className="space-y-4 text-xs max-h-[60vh] overflow-y-auto pr-1">

              {/* Naziv */}
              <div className="bg-muted/30 p-3.5 rounded-xl border border-border space-y-1">
                <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                  {t('listapartner.col_name', { defaultValue: 'Naziv Partnera' })}
                </span>
                {isEditingPartner ? (
                  <input
                    type="text"
                    className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                    value={editForm.naziv_partnera}
                    onChange={e => setEditForm({ ...editForm, naziv_partnera: e.target.value })}
                  />
                ) : (
                  <p className="font-bold text-foreground break-all text-sm">{selectedPartnerForInfo.naziv_partnera || t('common.nd', { defaultValue: 'N/D' })}</p>
                )}
              </div>

              {/* Tip / PIB / PDV */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('listapartner.type', { defaultValue: 'Tip Partnera' })}
                  </span>
                  {isEditingPartner ? (
                    <select
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none focus:ring-2 focus:ring-primary/20"
                      value={editForm.id_kategorije || editForm.tip_partnera}
                      onChange={handleEditTipChange}
                    >
                      <option value="">{t('listapartner.select_placeholder', { defaultValue: '-- Odaberi --' })}</option>
                      {STANDARD_TYPE_CODES.map(code => (
                        <option key={code} value={code}>{getTipPartneraLabel(code)}</option>
                      ))}
                      {kategorije.map(kat => (
                        <option key={kat.id} value={kat.id}>{kat.naziv_kategorije}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="inline-block bg-primary/10 text-primary font-bold px-2 py-0.5 rounded-md text-[11px]">
                      {getTipPartneraLabel(selectedPartnerForInfo.tip_partnera) || t('common.nd', { defaultValue: 'N/D' })}
                    </span>
                  )}
                </div>
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('listapartner.col_piva', { defaultValue: 'PIB / JIB' })}
                  </span>
                  {isEditingPartner ? (
                    <input
                      type="text"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.pib_jib}
                      onChange={e => setEditForm({ ...editForm, pib_jib: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground font-mono">{selectedPartnerForInfo.pib_jib || t('common.nd', { defaultValue: 'N/D' })}</p>
                  )}
                </div>
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('listapartner.pdv', { defaultValue: 'PDV Broj' })}
                  </span>
                  {isEditingPartner ? (
                    <input
                      type="text"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.pdv_broj}
                      onChange={e => setEditForm({ ...editForm, pdv_broj: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground font-mono">{selectedPartnerForInfo.pdv_broj || t('common.nd', { defaultValue: 'N/D' })}</p>
                  )}
                </div>
              </div>

              {/* Broj registracije */}
              <div className="bg-muted/30 p-3.5 rounded-xl border border-border space-y-1">
                <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                  {t('listapartner.broj_registracije', { defaultValue: 'Broj Registracije' })}
                </span>
                {isEditingPartner ? (
                  <input
                    type="text"
                    className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                    value={editForm.broj_registracije}
                    onChange={e => setEditForm({ ...editForm, broj_registracije: e.target.value })}
                  />
                ) : (
                  <p className="font-bold text-foreground font-mono">{selectedPartnerForInfo.broj_registracije || t('common.nd', { defaultValue: 'N/D' })}</p>
                )}
              </div>

              {/* Adresa / Grad / Poštanski broj */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('listapartner.adresa', { defaultValue: 'Adresa' })}
                  </span>
                  {isEditingPartner ? (
                    <input
                      type="text"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.adresa}
                      onChange={e => setEditForm({ ...editForm, adresa: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground">{selectedPartnerForInfo.adresa || t('common.nd', { defaultValue: 'N/D' })}</p>
                  )}
                </div>
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('listapartner.postanski_broj', { defaultValue: 'Poštanski Broj' })}
                  </span>
                  {isEditingPartner ? (
                    <input
                      type="text"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.postanski_broj}
                      onChange={e => setEditForm({ ...editForm, postanski_broj: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground">{selectedPartnerForInfo.postanski_broj || t('common.nd', { defaultValue: 'N/D' })}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('listapartner.grad', { defaultValue: 'Grad' })}
                  </span>
                  {isEditingPartner ? (
                    <input
                      type="text"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.grad}
                      onChange={e => setEditForm({ ...editForm, grad: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground">{selectedPartnerForInfo.grad || t('common.nd', { defaultValue: 'N/D' })}</p>
                  )}
                </div>
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('listapartner.drzava', { defaultValue: 'Država' })}
                  </span>
                  {isEditingPartner ? (
                    <input
                      type="text"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.drzava}
                      onChange={e => setEditForm({ ...editForm, drzava: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground">{selectedPartnerForInfo.drzava || t('common.nd', { defaultValue: 'N/D' })}</p>
                  )}
                </div>
              </div>

              {/* Email / Telefon / Web */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('listapartner.email', { defaultValue: 'Email' })}
                  </span>
                  {isEditingPartner ? (
                    <input
                      type="email"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.email}
                      onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground break-all">{selectedPartnerForInfo.email || t('common.nd', { defaultValue: 'N/D' })}</p>
                  )}
                </div>
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('listapartner.telefon', { defaultValue: 'Telefon' })}
                  </span>
                  {isEditingPartner ? (
                    <input
                      type="text"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.telefon}
                      onChange={e => setEditForm({ ...editForm, telefon: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground">{selectedPartnerForInfo.telefon || t('common.nd', { defaultValue: 'N/D' })}</p>
                  )}
                </div>
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('listapartner.web', { defaultValue: 'Web Stranica' })}
                  </span>
                  {isEditingPartner ? (
                    <input
                      type="text"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.web_stranica}
                      onChange={e => setEditForm({ ...editForm, web_stranica: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground break-all">{selectedPartnerForInfo.web_stranica || t('common.nd', { defaultValue: 'N/D' })}</p>
                  )}
                </div>
              </div>

              {/* Banka / IBAN */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('listapartner.naziv_banke', { defaultValue: 'Naziv Banke' })}
                  </span>
                  {isEditingPartner ? (
                    <input
                      type="text"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.naziv_banke}
                      onChange={e => setEditForm({ ...editForm, naziv_banke: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground">{selectedPartnerForInfo.naziv_banke || t('common.nd', { defaultValue: 'N/D' })}</p>
                  )}
                </div>
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('listapartner.bankovni_racun', { defaultValue: 'Bankovni Račun (IBAN)' })}
                  </span>
                  {isEditingPartner ? (
                    <input
                      type="text"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.bankovni_racun}
                      onChange={e => setEditForm({ ...editForm, bankovni_racun: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground font-mono">{selectedPartnerForInfo.bankovni_racun || t('common.nd', { defaultValue: 'N/D' })}</p>
                  )}
                </div>
              </div>

              {/* Kontakt osoba / Telefon kontakt osobe */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('listapartner.kontakt_osoba', { defaultValue: 'Kontakt Osoba' })}
                  </span>
                  {isEditingPartner ? (
                    <input
                      type="text"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.kontakt_osoba}
                      onChange={e => setEditForm({ ...editForm, kontakt_osoba: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground">{selectedPartnerForInfo.kontakt_osoba || t('common.nd', { defaultValue: 'N/D' })}</p>
                  )}
                </div>
                <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                  <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                    {t('listapartner.kontakt_telefon', { defaultValue: 'Telefon Kontakt Osobe' })}
                  </span>
                  {isEditingPartner ? (
                    <input
                      type="text"
                      className="w-full p-2 bg-background border border-border rounded-lg text-xs font-bold outline-none"
                      value={editForm.kontakt_telefon}
                      onChange={e => setEditForm({ ...editForm, kontakt_telefon: e.target.value })}
                    />
                  ) : (
                    <p className="font-bold text-foreground">{selectedPartnerForInfo.kontakt_telefon || t('common.nd', { defaultValue: 'N/D' })}</p>
                  )}
                </div>
              </div>

              {/* Datum kreiranja */}
              <div className="bg-muted/30 p-3 rounded-xl border border-border space-y-1">
                <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                  {t('listapartner.col_date', { defaultValue: 'Datum Dodavanja' })}
                </span>
                <p className="font-bold text-foreground">
                  {selectedPartnerForInfo.created_at ? new Date(selectedPartnerForInfo.created_at).toLocaleString() : t('common.nd', { defaultValue: 'N/D' })}
                </p>
              </div>

              {/* Napomena */}
              <div className="bg-muted/30 p-3.5 rounded-xl border border-border space-y-1">
                <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest block">
                  {t('listapartner.napomena', { defaultValue: 'Napomena' })}
                </span>
                {isEditingPartner ? (
                  <textarea
                    className="w-full p-2.5 bg-background border border-border rounded-lg text-xs outline-none min-h-[70px]"
                    value={editForm.napomena}
                    onChange={e => setEditForm({ ...editForm, napomena: e.target.value })}
                  />
                ) : (
                  <p className="font-medium text-muted-foreground whitespace-pre-wrap leading-relaxed">
                    {selectedPartnerForInfo.napomena || t('listapartner.no_notes', { defaultValue: 'Nema dodanih napomena.' })}
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-2 border-t border-border">
              {isEditingPartner ? (
                <>
                  <button
                    onClick={() => setIsEditingPartner(false)}
                    disabled={savingPartner}
                    className="px-5 py-2.5 border border-border rounded-xl text-xs font-bold uppercase tracking-wider text-muted-foreground hover:bg-muted hover:text-foreground transition-all disabled:opacity-50"
                  >
                    {t('common.cancel', { defaultValue: 'Otkaži' })}
                  </button>
                  <button
                    onClick={handleSavePartnerEdits}
                    disabled={savingPartner}
                    className="bg-primary text-primary-foreground px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-all shadow-md shadow-primary/20 flex items-center gap-2 disabled:opacity-50"
                  >
                    {savingPartner
                      ? <><Icon name="Loader2" size={16} className="animate-spin" />{t('common.saving', { defaultValue: 'Spremanje...' })}</>
                      : <><Icon name="Check" size={16} />{t('common.save', { defaultValue: 'Spremi' })}</>
                    }
                  </button>
                </>
              ) : (
                <div className="flex items-center justify-between w-full">
                  <button
                    onClick={() => navigate(`/partner/${selectedPartnerForInfo.id}`)}
                    className="flex items-center gap-1.5 px-4 py-2.5 bg-muted border border-border rounded-xl text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted/80 transition-all"
                  >
                    <Icon name="FolderOpen" size={16} /> {t('listapartner.view_documents', { defaultValue: 'Dokumenti Partnera' })}
                  </button>
                  <button
                    onClick={() => { setSelectedPartnerForInfo(null); setIsEditingPartner(false); }}
                    className="px-5 py-2.5 border border-border rounded-xl text-xs font-bold uppercase tracking-wider text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
                  >
                    {t('common.close', { defaultValue: 'Zatvori' })}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnersCompanyPage;